<template>
  <div class="full-screen-banner-box" :style="canvasStyle">
    <img
        v-if="!isVideo"
        ref="imageElement"
        :src="resolvedContentUrl"
        @error="onMediaError"
    />
    <video
        v-else
        ref="videoElement"
        :src="resolvedContentUrl"
        preload="auto"
        playsinline
        @error="onMediaError"
    />
    <div class="countdown" :style="timerStyle">{{ secondsLeft }}</div>
  </div>
</template>

<script>
import { reportAdEvent } from '../services/adReport'
import { reportAdFailure } from '../services/adFailure'
import mediaFallback from '../mixins/mediaFallback'

export default {
  name: 'FullScreenBannerBox',
  mixins: [mediaFallback],
  data () {
    return {
      secondsLeft: this.duration,
      countdownInterval: null,
      finishTimeoutId: null
    }
  },
  props: {
    contentUrl: {
      type: String,
      required: true
    },
    universalSettings: {
      type: Object,
      required: false,
      default: () => ({})
    },
    endUrl: {
      type: String,
      required: true
    },
    startUrl: {
      type: String,
      default: ''
    },
    duration: {
      type: Number,
      required: true
    },
    // Signed route on the monolith, same one VideoBox reports to. Empty is a
    // no-op in reportAdFailure, so a caller that does not pass it loses the
    // server-side report and keeps the Sentry one.
    failUrl: {
      type: String,
      default: ''
    },
    taskId: {
      type: [Number, String],
      default: null
    }
  },
  mounted () {
    try {
      this._finished = false
      // How long the creative hung before failing, for reportAdFailure.
      this._mountedAt = Date.now()
      this.reportStart()
      this.startCountdown()
      this.scheduleFinish()

      // Only try to play video if it's actually a video
      if (this.isVideo) {
        this.$nextTick(() => {
          this.playVideo()
        })
      }

    } catch (e) {
      console.error('finishing because of error', e)
      this.emitFinished()
    }
  },
  computed: {
    safeUniversalSettings () {
      return this.universalSettings || {}
    },
    canvasStyle () {
      return this.safeUniversalSettings.canvasStyle || {}
    },
    timerStyle () {
      return this.safeUniversalSettings.timerStyle || {}
    },
    isVideo () {
      const media = this.safeUniversalSettings.media?.[0]
      const t = (media?.type || '').toLowerCase()
      if (t === 'video') return true
      if (t === 'image') return false
      return /\.(webm|mp4|mov|m4v|ogg)$/i.test(this.contentUrl)
    }
  },
  methods: {
    /**
     * The creative did not load. Retry once on the other zone; if that is not
     * possible or has already been done, report it rather than counting down
     * over an empty frame - which is what this component did until 23.09.2026.
     */
    onMediaError (event) {
      if (this.tryMediaFallback()) return

      const el = event && event.target ? event.target : null

      reportAdFailure('media_error', {
        failUrl: this.failUrl,
        taskId: this.taskId,
        contentUrl: this.resolvedContentUrl,
        video: el && el.tagName === 'VIDEO' ? el : null,
        startedAt: this._mountedAt || null
      })
    },

    startCountdown () {
      this.countdownInterval = setInterval(() => {
        if (this.secondsLeft > 0) {
          this.secondsLeft--
        }
      }, 1000)
    },

    scheduleFinish () {
      this.finishTimeoutId = setTimeout(() => {
        this.finishTimeoutId = null
        this.finish()
      }, this.duration * 1000)
    },

    async playVideo () {
      const videoElement = this.$refs.videoElement
      if (!videoElement) return

      try {
        await videoElement.play()
      } catch (e) {
        console.warn('Video autoplay failed:', e)

        try {
          videoElement.pause()
          videoElement.muted = true
          videoElement.defaultMuted = true
          await videoElement.play()
        } catch (retryError) {
          console.warn('Muted autoplay also failed:', retryError)
        }
      }
    },

    reportStart () {
      return reportAdEvent(this.startUrl, 'startUrl')
    },

    reportEnd () {
      return reportAdEvent(this.endUrl, 'endUrl')
    },

    async finish () {
      try {
        await this.reportEnd()
      } catch (e) {
        console.error('Error calling end URL:', e)
        // Continue with finish even if API call fails
      } finally {
        this.emitFinished()
      }
    },

    emitFinished () {
      if (this._finished) return
      this._finished = true
      this.$emit('finished', this.$vnode.key)
    }
  },
  beforeDestroy() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval)
      this.countdownInterval = null
    }
    if (this.finishTimeoutId) {
      clearTimeout(this.finishTimeoutId)
      this.finishTimeoutId = null
    }
  }
}
</script>

<style lang="scss">
.full-screen-banner-box {
  position: absolute;
  height: 100%;
  width: 100%;
  background-color: rgba(0, 0, 0, 0.9);
  z-index: 1000;
  padding: 0;

  video, img {
    width: 100%;
    max-height: 100%;
    padding: 0;
    position: relative;
    z-index: 5;
    object-fit: contain;
  }
}

.countdown {
  background-color: rgb(182, 118, 230);
  border-radius: 18px;
  color: #fff;
  font-size: 1em;
  font-weight: 300;
  font-family: monospace;
  height: 32px;
  line-height: 32px;
  padding: 0px 7px;
  position: absolute;
  min-width: 32px;
  right: 10px;
  top: 10px;
  text-align: center;
  z-index: 10;
}
</style>